<template>
    <section v-if="displayedBenefits.length > 0" class="mt-8">
        <h4 class="text-base font-medium leading-6 text-slate-800 dark:text-slate-100 mb-6">
            Card benefits
        </h4>

        <!-- Benefits Status Bar -->
        <a
            v-if="benefitsSummary?.total_annual_value > 0 && isCardTracked"
            :href="trackBenefitsLink"
            class="flex items-center justify-between bg-white dark:bg-slate-800 border border-slate-200 rounded-lg px-4 py-2.5 mb-3 hover:bg-slate-50 transition-colors"
        >
            <div class="flex-1">
                <!-- Usage Summary -->
                <div class="flex items-center justify-between mb-1">
                    <p class="text-sm font-medium leading-5 text-slate-800 dark:text-slate-100">
                        {{ $filters.formatSmartMoney(displayedUsedAmount) }}
                        of
                        {{
                            $filters.formatSmartMoney(
                                benefitsSummary?.total_annual_value ?? 0
                            )
                        }}
                        redeemed this year
                    </p>
                    <span
                        class="text-sm font-medium leading-5 text-slate-600 flex items-center gap-1"
                    >
                        Track benefits
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M6 12L10 8L6 4"
                                stroke="currentColor"
                                stroke-width="1.5"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </span>
                </div>

                <!-- Progress Bar -->
                <div class="w-full rounded-[99px] h-1.5 bg-[#FED1C7]">
                    <div
                        class="h-1.5 rounded-[99px] bg-10x-orange transition-all duration-300"
                        :style="{
                            width: `${Math.min(displayedUtilizationPercentage, 100)}%`,
                        }"
                        :aria-label="`${displayedUtilizationPercentage}% utilized`"
                    ></div>
                </div>
            </div>
        </a>

        <!-- Benefits List -->
        <dl class="mb-6">
            <div
                v-for="(benefit, index) in displayedBenefits"
                :key="benefit.id"
                class="py-3"
                :class="{
                    'fade-bottom':
                        !showAll && index === 4 && displayedBenefits.length > 5,
                }"
            >
                <div class="flex items-start gap-3">
                    <!-- Benefit Icon -->
                    <BenefitIcon
                        :category="
                            (benefit.current_version || benefit.currentVersion)
                                ?.category || benefit.category
                        "
                        :title="
                            (benefit.current_version || benefit.currentVersion)
                                ?.benefit_title ||
                            benefit.benefit_title ||
                            ''
                        "
                        :description="
                            (benefit.current_version || benefit.currentVersion)
                                ?.benefit_description ||
                            benefit.benefit_description ||
                            ''
                        "
                        container-class="w-8 h-8"
                        svg-class="w-4 h-4 text-slate-600 dark:text-slate-300"
                    />
                    <!-- Benefit Content -->
                    <div class="flex-1 min-w-0">
                        <dt class="text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                            {{
                                (
                                    benefit.current_version ||
                                    benefit.currentVersion
                                )?.benefit_title || benefit.benefit_title
                            }}
                        </dt>
                        <dd class="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                            {{
                                (
                                    benefit.current_version ||
                                    benefit.currentVersion
                                )?.benefit_description ||
                                benefit.benefit_description
                            }}
                        </dd>
                    </div>
                </div>
            </div>
        </dl>

        <!-- Show More/Less Toggle -->
        <div v-if="activeBenefits.length > 5" class="text-center">
            <button
                v-if="!showAll"
                @click="showAll = true"
                class="text-sm text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:text-slate-100 font-medium underline transition-colors"
            >
                Show all {{ activeBenefits.length }} benefits
            </button>
            <button
                v-else
                @click="showAll = false"
                class="text-sm text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:text-slate-100 font-medium underline transition-colors"
            >
                Show less
            </button>
        </div>
    </section>
</template>

<script>
import BenefitIcon from '@/components/card-benefits/benefit-icon.vue';

export default {
    name: 'CardBenefitsList',
    components: {
        BenefitIcon,
    },
    props: {
        benefits: {
            type: Array,
            default: () => [],
        },
        benefitsSummary: {
            type: Object,
            default: null,
        },
        connectedAccountId: {
            type: Number,
            default: null,
        },
        isSubscribed: {
            type: Boolean,
            default: true,
        },
        trackedConnectedAccountIds: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            showAll: false,
        };
    },
    computed: {
        activeBenefits() {
            if (!this.benefits?.length) {
                return [];
            }
            // Filter to only show benefits with an active version
            // Check both snake_case and camelCase since serialization may vary
            return this.benefits.filter(
                (b) => b.current_version || b.currentVersion
            );
        },
        displayedBenefits() {
            return this.showAll
                ? this.activeBenefits
                : this.activeBenefits.slice(0, 5);
        },
        utilizationPercentage() {
            if (!this.benefitsSummary?.total_annual_value) return 0;
            return Math.round(
                (this.benefitsSummary.used_ytd /
                    this.benefitsSummary.total_annual_value) *
                    100
            );
        },
        isCardTracked() {
            if (this.isSubscribed) return true;
            if (!this.connectedAccountId) return false;
            return this.trackedConnectedAccountIds.includes(
                this.connectedAccountId
            );
        },
        displayedUsedAmount() {
            if (!this.isCardTracked) return 0;
            return this.benefitsSummary?.used_ytd ?? 0;
        },
        displayedUtilizationPercentage() {
            if (!this.isCardTracked) return 0;
            return this.utilizationPercentage;
        },
        trackBenefitsLink() {
            if (this.isCardTracked && this.connectedAccountId) {
                return `/card-benefits?card=${this.connectedAccountId}`;
            }
            return '/card-benefits';
        },
    },
    watch: {
        benefits: {
            immediate: true,
            handler() {
                // Reset showAll when benefits change (e.g., switching cards)
                this.showAll = false;
            },
        },
    },
};
</script>

<style scoped>
.fade-bottom {
    position: relative;
}

.fade-bottom::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 40px;
    background: linear-gradient(to bottom, transparent, white);
    pointer-events: none;
}
</style>
